import { useCallback, useEffect } from "react";
import { useRouter } from "@tanstack/react-router";
import { useAuth } from "../context/AuthContext";
import { useDevBoardNotifications } from "../pages/DevBoard/hooks/useDevBoardNotifications";
import { showActionToast } from "../utils/notify";
import { showDevBoardOsNotification } from "../utils/devBoardOsNotifications";
import { primeLivechatOsNotificationPermission } from "../utils/livechatOsNotifications";
import type { DevBoardMention } from "../pages/DevBoard/types";

const CURSOR_STORAGE_PREFIX = "di_dev_board_mention_last_seen_id";

/** Limited admins reach the Dev Board too, and AuthContext does not carry that
 * flag — the sidebar reads it from storage the same way. */
function isLimitedAdmin(): boolean {
  try {
    return window.localStorage.getItem("is_limited_admin") === "true";
  } catch {
    return false;
  }
}

function readCursor(storageKey: string): number {
  try {
    const parsed = Number(window.localStorage.getItem(storageKey));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeCursor(storageKey: string, mentionId: number): void {
  try {
    window.localStorage.setItem(storageKey, String(mentionId));
  } catch {
    // Ignore storage failures — worst case the toast repeats after a reload.
  }
}

/**
 * Toasts new Dev Board mentions, the same way ReportCommentNotifications does
 * for conversation report comments: poll, announce only mentions newer than the
 * cursor this browser already showed, and deep-link into the ticket (which is
 * also what marks the mention read).
 */
export default function DevBoardMentionNotifications() {
  const { history } = useRouter();
  const auth = useAuth() || {};
  const isAuthenticated = auth.isAuthenticated === true;
  const canSeeDevBoard = auth.isAdmin === true || isLimitedAdmin();
  const cursorStorageKey = `${CURSOR_STORAGE_PREFIX}:${auth.user?.sub || "local"}`;

  const showToast = useCallback(
    (mention: DevBoardMention) => {
      showActionToast({
        id: `di-dev-board-mention-${mention.id}`,
        title: mention.authorName
          ? `${mention.authorName} tagged you on the Dev Board`
          : "You were tagged on the Dev Board",
        description: `#${mention.issueNumber} ${mention.issueTitle ?? ""}`,
        actionLabel: "Open ticket",
        onAction: () => history.push(`/dev-board?ticket=${mention.issueNumber}`),
      });
    },
    [history],
  );

  const notificationsQuery = useDevBoardNotifications(isAuthenticated && canSeeDevBoard);
  const mentions = notificationsQuery.data?.mentions;

  useEffect(() => {
    if (!mentions || mentions.length === 0) return;

    const lastSeen = readCursor(cursorStorageKey);
    const fresh = mentions.filter((mention) => mention.id > lastSeen);
    if (fresh.length === 0) return;

    // Persist before toasting so a reload mid-toast never repeats it. The
    // cursor advances past participant rows too, so they are never re-examined
    // — they still count toward the sidebar badge, which reads the unread rows
    // directly rather than this cursor.
    writeCursor(cursorStorageKey, Math.max(...fresh.map((mention) => mention.id)));

    // Only being tagged is worth interrupting for. A reply on a ticket you
    // merely commented on moves the counter and nothing else, otherwise a busy
    // ticket toasts everyone who ever touched it.
    const worthAToast = fresh.find((mention) => mention.reason !== "participant");
    if (!worthAToast) return;
    showToast(worthAToast);
    // Complementary surfaces: the toast covers a visible tab, the OS
    // notification a hidden one (it shows nothing while the tab has focus —
    // same rule as livechat).
    showDevBoardOsNotification({
      title: worthAToast.authorName
        ? `${worthAToast.authorName} tagged you on the Dev Board`
        : "You were tagged on the Dev Board",
      body: `#${worthAToast.issueNumber} ${worthAToast.issueTitle ?? ""}`.trim(),
      eventKey: String(worthAToast.id),
      onOpen: () => history.push(`/dev-board?ticket=${worthAToast.issueNumber}`),
    });
  }, [cursorStorageKey, history, mentions, showToast]);

  useEffect(() => {
    if (!isAuthenticated || !canSeeDevBoard) return undefined;
    // Ask for OS-notification permission on the next click or keypress, the
    // same way livechat does. Permission is per-origin, so whichever feature
    // primes it first covers both; this is a no-op once decided.
    return primeLivechatOsNotificationPermission();
  }, [canSeeDevBoard, isAuthenticated]);

  return null;
}
